import { createClient } from '@supabase/supabase-js';

// ----------------------------------------------------------------------
// Single browser Supabase Auth client for all three portals (intranet /
// vendors / clients). Only the PUBLIC anon key is used here — never the
// service-role key, which stays server-side in lingoserver.
//
// Env (CRA, must be REACT_APP_*-prefixed and present at build time):
//   REACT_APP_SUPABASE_URL
//   REACT_APP_SUPABASE_ANON_KEY
// ----------------------------------------------------------------------

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  // Loud in dev, harmless string in prod builds — auth calls will fail fast
  // with a clear message instead of a cryptic network error.
  // eslint-disable-next-line no-console
  console.error(
    'Supabase env missing: set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY',
  );
}

/** localStorage key of the persisted session. */
export const AUTH_STORAGE_KEY = 'lingoyou.auth';
/** localStorage flag: the current session came from a recovery/invite link. */
export const AUTH_RECOVERY_KEY = 'lingoyou.auth.recovery';

// ----------------------------------------------------------------------
// What this page load carried in its URL fragment, if it came from an email
// link.
//
// Read synchronously, BEFORE createClient: supabase-js consumes the fragment
// while it initialises and blanks it the moment the tokens are exchanged, so a
// React effect that looks later usually finds nothing. Three things are kept:
//   type      GoTrue's `type` — 'recovery' | 'invite' | 'signup' | 'magiclink'
//             | 'email_change' — which is what lets the app tell a link that
//             exists only so a password can be set from a magic link, which is
//             an ordinary sign-in
//   fragment  the raw fragment, so a link that belongs to another portal can
//             be handed over untouched
//   error     GoTrue's error_description for a dead link (expired, used, or
//             consumed by a mail scanner's preview fetch), which otherwise
//             arrives with no visible effect at all
// ----------------------------------------------------------------------
function readAuthLink() {
  const none = { type: null, fragment: null, error: null };
  try {
    const raw = (window.location.hash || '').replace(/^#/, '');
    if (!raw) return none;
    const fragment = new URLSearchParams(raw);
    if (fragment.get('access_token')) {
      return { type: fragment.get('type') || 'unknown', fragment: raw, error: null };
    }
    const error =
      fragment.get('error_description') ||
      fragment.get('error_code') ||
      fragment.get('error');
    if (error) return { type: null, fragment: raw, error };
    return none;
  } catch {
    return none;
  }
}

export const AUTH_LINK = readAuthLink();
/** 'recovery' | 'invite' | 'signup' | 'magiclink' | 'email_change' | null */
export const AUTH_LINK_TYPE = AUTH_LINK.type;

export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    // Persist the session and refresh access tokens automatically; this
    // replaces the old 60s /verify-token polling loop entirely.
    persistSession: true,
    autoRefreshToken: true,
    // Consume the tokens Supabase appends to the redirect of every email link
    // (#access_token=…&type=recovery|invite|signup|magiclink), then clean the
    // URL.
    detectSessionInUrl: true,
    // Implicit, not PKCE — deliberately. supabase-js refuses to consume a
    // fragment-style callback when it is configured for PKCE ("Not a valid
    // PKCE flow url"), and the links lingoserver sends through the admin API —
    // the invite a new client gets when a quote is won — can ONLY be
    // fragment-style: PKCE needs a code verifier held by the browser that
    // started the flow, and no browser starts an admin invite. Under PKCE those
    // invites died silently on arrival. PKCE also meant a recovery or
    // confirmation link only worked in the browser that requested it — opened
    // on a phone after a request from a desktop, it failed with no message.
    // With the implicit flow every link works from any device.
    flowType: 'implicit',
    storageKey: AUTH_STORAGE_KEY,
  },
});

/** Convenience: current access token (or null) for non-Apollo callers. */
export async function getAccessToken() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
}

export default supabase;
